import { Divider, Headline, Layout, Text, TextLink } from '@oneaudi/unified-web-alpha-components';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import styled from 'styled-components';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useAppState } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { StyledPriceDetailContainer } from '~/app/pages/summary/dialogs/price-detail/core/styles/price-details-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { LayerType } from '~/app/tracking/feld-m-types';
import { getFilteredPriceParts } from '~/app/utils/FilteredPriceParts';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';

export function PriceDetailsLegalCanadaDialog({ open, onClose }: DialogProps) {
	const { config } = useMarketConfig();
	const { financingData } = useAppState();
	const [financialError] = useAtom(state.financialError);
	useEffect(() => {
		if (open) {
			appTrackingService.updateLayerComponent(LayerType.PriceDetailsLayer, 'open');
			appTrackingService.viewChangeEvent({
				eventName: `configurator - ${LayerType.PriceDetailsLayer}`,
				componentName: 'price-details-layer',
			});
		}
	}, [open]);
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			{config.enableFinancingServices &&
			financingData?.financing &&
			financingData?.financingDetails &&
			!financialError ? (
				<PriceDetailsAsBuiltDialogContent />
			) : (
				<PriceDetailsAsBuiltFinanceDisabledDialogContent />
			)}
		</Dialog>
	);
}

function PriceDetailsAsBuiltDialogContent() {
	const { catalog } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n } = useI18n();
	const { financingData } = useAppState();
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const [preset] = useAtom(state.fsagFinancialCalculatorSettings);
	const product = preset ?? { '@ID': defaultProduct };
	const financialProducts = useAtomValue(state.financialProducts);
	const { openDialog, closeDialog } = useDialogs();
	const { getReference } = useFootnotes();
	const { priceDateRemark } = usePriceDetailRemarks();

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const financingProducts = financingData?.financingDetails?.detailGroups
		.find((group) => group.key.toLowerCase().replace(/ /g, '') === 'products')
		?.items /* remove PriceAsBuiltModal key from list because this shouldn't be shown in price details */
		.filter((d) => d.key.toLocaleLowerCase() !== 'priceasbuiltmodal');

	const financingDetails = financingData?.financingDetails?.detailGroups.find(
		(group) => group.key.toLowerCase().replace(/ /g, '') === 'pricedetails',
	)?.items;

	return (
		<StyledPriceDetailContainer>
			<div tabIndex={0} className="header-section">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'normal' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
					&nbsp;
					<FootnoteReferenceNgc
						reference={getReference(CAE_CONFIGUREDCAR_REMARK)}
						ariaLabel={i18n('ngc.a11y.footnotes.cae')}
					/>
				</Headline>
			</div>
			<div tabIndex={0} className="title-section">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					{' '}
					{i18n('ngc.priceDetailsCa.title', {
						type:
							financialProducts.find((prod) => {
								return product && prod.key === product['@ID'];
							})?.val ?? i18n('ngc.priceDetails.privateLeasing'),
					})}
				</Text>
			</div>

			<ul className="price-list">
				{financingProducts &&
					financingProducts?.map((part) => (
						<PriceSectionLegalCanada
							key={part.key}
							title={part.label}
							price={part.formattedValue}
							disclaimer={null}
						/>
					))}
			</ul>
			<StyledTextLink
				size="md"
				onClick={() => {
					closeDialog('priceDetails');
					openDialog('monthlyPriceRatesFsag');
				}}
				variant="standalone">
				{i18n('ngc.actions.changeLeasingRate')}
			</StyledTextLink>
			<StyledDivider />

			{financingDetails &&
				financingDetails.map((part) => (
					<PriceSectionLegalCanada
						key={part.key}
						title={part.label}
						price={part.formattedValue}
						disclaimer={null}
						bold
					/>
				))}

			{financingData?.financingDetails?.disclaimers && (
				<Layout direction={'column'} rowGap="16px" margin={['20px', '0', '0', '0']}>
					{financingData?.financingDetails?.disclaimers?.map((disclaimer) => {
						return (
							<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
								<RenderHtml key={disclaimer.key} tabIndex={0} html={disclaimer.text} />
							</Text>
						);
					})}
				</Layout>
			)}
			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					{}
					<RenderHtml tabIndex={0} html={priceDateRemark} />
				</Text>
			)}
		</StyledPriceDetailContainer>
	);
}

const StyledDivider = styled(Divider)`
	margin: 20px 0;
`;

const StyledTextLink = styled(TextLink)`
	@media screen and (min-width: 768px) {
		margin-left: auto;
	}
`;

export function PriceSectionLegalCanada({
	pricePartType,
	title,
	price,
	bold,
	disclaimer,
}: {
	pricePartType?: string;
	/** Translated title of this section.  */
	title: string;
	/** Formatted price of this part. */
	price: string | null | undefined;
	bold?: boolean;
	disclaimer: string | null | undefined;
}) {
	const { i18n } = useI18n();
	const { addFootnote, getReference } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;

	if (i18nKey in i18nKeys) {
		addFootnote(i18nKey, i18n(i18nKey));
	}

	if (disclaimer) {
		addFootnote('p-disclaimer', disclaimer);
	}

	return (
		<div tabIndex={0}>
			<Layout direction={{ default: 'column', md: 'row' }} columnGap="32px" justify="space-between">
				<Text variant={{ type: 'copy', order: '1', style: bold ? 'bold' : 'normal' }}>
					{title}

					<FootnoteReferenceNgc
						reference={getReference(i18nKey)}
						ariaLabel={i18n('ngc.a11y.footnotes.price.part', { part: title })}
					/>

					<FootnoteReferenceNgc
						reference={getReference('p-disclaimer')}
						ariaLabel={i18n('ngc.a11y.footnotes.price.disclaimer')}
					/>
				</Text>
				<Text variant={{ type: 'copy', order: '1', style: bold ? 'bold' : 'normal' }}>{price}</Text>
			</Layout>
		</div>
	);
}

function PriceDetailsAsBuiltFinanceDisabledDialogContent() {
	const { catalog } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n } = useI18n();
	const { formatCurrency } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { priceDateRemark } = usePriceDetailRemarks();

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	const priceParts = getFilteredPriceParts(priceSummary, config);

	return (
		<StyledPriceDetailContainer>
			<div tabIndex={0} className="header-section">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'normal' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
				</Headline>
			</div>
			<div tabIndex={0} className="title-section">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			<ul className="price-list">
				{priceParts?.map((part) => (
					<PriceSection
						key={part.type}
						pricePartType={part.type}
						title={i18n(`ngc.priceDetails.${part.type}` as keyof typeof i18nKeys)}
						price={formatCurrency(part.price, vatConfig.priceParts[part.type])}
					/>
				))}
			</ul>

			<Divider />

			<PriceSection
				pricePartType="total"
				title={i18n('ngc.priceDetails.priceAsBuilt')}
				price={formatCurrency(catalog.priceSummary?.totalPrice, vatConfig.totalPrice)}
				bold
			/>

			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml tabIndex={0} html={priceDateRemark} />
				</Text>
			)}
		</StyledPriceDetailContainer>
	);
}

function PriceSection({
	pricePartType,
	title,
	price,
	bold,
}: {
	pricePartType: string;
	title: string;
	price: string | null | undefined;
	bold?: boolean;
}) {
	const { i18n } = useI18n();
	const { addFootnote, getReference } = useFootnotes();

	const i18nKey = `ngc.remarks.pricePart.${pricePartType}` as keyof typeof i18nKeys;

	if (i18nKey in i18nKeys) {
		addFootnote(i18nKey, i18n(i18nKey));
	}

	return (
		<Layout direction={{ default: 'column', md: 'row' }} columnGap="32px" justify="space-between">
			<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>
				{title}
				<FootnoteReferenceNgc
					reference={getReference(i18nKey)}
					ariaLabel={i18n('ngc.a11y.footnotes.price.part', { part: title })}
				/>
			</Text>
			<Text variant={{ type: 'copy', order: '2', style: bold ? 'bold' : 'normal' }}>{price}</Text>
		</Layout>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n } = useI18n();

	return {
		priceDateRemark: `*${i18n('ngc.summary.remark')}`,
	};
}
